import { observer } from "mobx-react-lite";
import { toastModule } from "@/modules/toast";
import { MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store/utils/hooks";
import styled from "@emotion/styled";
import {
  MDSIconButton,
  MDSRowItemSlotName,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSText,
  MDSTextVariant,
} from "@/mds";

interface CopilotVotingButtonsProps {
  itemId: string;
  topicId: string;
  topicItemId: string | null;
  itemRelevanceScore?: number;
  feedbackClass: string;
  alwaysVisible?: boolean;
  trackUpvote?: () => void;
  trackDownvote?: () => void;
  slot?: MDSRowItemSlotName;
}

enum TopicItemVote {
  None,
  Upvote,
  Downvote,
}

export const CopilotVotingButtons = observer<CopilotVotingButtonsProps>(
  function CopilotVotingButtons({
    itemId,
    topicId,
    topicItemId,
    itemRelevanceScore,
    feedbackClass,
    alwaysVisible = false,
    trackUpvote,
    trackDownvote,
  }) {
    const { store, pageStore } = useAppStore();
    const debugModeEnabled = store.debug.debugModeEnabled;
    const submitTopicFeedback = pageStore.copilotView.submitTopicFeedback;
    const submitTopicItemFeedback = pageStore.copilotView.submitTopicItemFeedback;
    const highlightedItemId = pageStore.copilotView.highlightedRowId;
    const highlightedTopicId = pageStore.copilotView.highlightedTopicId;

    const isVisible =
      (itemId === highlightedItemId && highlightedTopicId == topicId) || alwaysVisible;

    const handleTopicVote = async (event: React.MouseEvent, voteType: TopicItemVote) => {
      event.preventDefault();
      event.stopPropagation();
      const additionalComments = prompt("Please enter reasoning for vote.") || null;
      if (feedbackClass === "topic") {
        await submitTopicFeedback(voteType, topicId, additionalComments);
        pageStore.copilotView.addToVotedIds(topicId);
      } else {
        if (!topicItemId) return;
        await submitTopicItemFeedback(voteType, topicItemId, additionalComments);
        pageStore.copilotView.addToVotedIds(topicItemId);
      }
      const voteTypeString = voteType === TopicItemVote.Upvote ? "good" : "bad";
      const resultName = feedbackClass === "topic" ? "topic" : "result";
      toastModule.triggerToast({ content: `Marked ${resultName} as ${voteTypeString}` });
    };

    const buttonsDisabled = pageStore.copilotView.votedIds.has(
      feedbackClass === "topic" ? topicId : topicItemId!
    );

    if (!isVisible && debugModeEnabled)
      return <MDSText variant={MDSTextVariant.TextXs}>{itemRelevanceScore}</MDSText>;

    if (!isVisible) return null;
    return (
      <VotingButtonContainer>
        <MDSTooltip
          config={{
            label: "Mark as good",
            placement: MDSTooltipPlacement.Left,
          }}
        >
          <VotingButton
            iconKind={MdsIconKind.Upvote}
            isDisabled={buttonsDisabled}
            onClick={e => {
              handleTopicVote(e, TopicItemVote.Upvote);
              trackUpvote?.();
            }}
          />
        </MDSTooltip>
        <MDSTooltip
          config={{
            label: "Mark as bad",
            placement: MDSTooltipPlacement.Left,
          }}
        >
          <VotingButton
            iconKind={MdsIconKind.Downvote}
            isDisabled={buttonsDisabled}
            onClick={e => {
              handleTopicVote(e, TopicItemVote.Downvote);
              trackDownvote?.();
            }}
          />
        </MDSTooltip>
      </VotingButtonContainer>
    );
  }
);

const VotingButton = styled(MDSIconButton)`
  padding: 4px;
  display: inline-block;
`;

const VotingButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxs};
  align-self: center;
  margin-top: ${({ theme }) => `-${theme.spacing.xxs}`};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;
